import produce from 'immer';
import { handleActions } from 'redux-actions';
import { clone, filter } from 'lodash';
import { CONFIRM_TAG_LIST_TYPE, STORE_TAG_GROUP_KIND_LIST } from '../../../constants/service/app/tagConstants';
import { updateStoreWithPath } from '../../../lib/common';
import actions from './actions';
import {
  BRAND_SUMMARY_BOX_MODEL,
  BRAND_DETAIL_BALANCE_MODEL,
  BRAND_DETAIL_BRAND_MODEL,
  BRAND_DETAIL_BUSINESS_MODEL,
  BRAND_DETAIL_ORDER_BALANCE_CALCULATE_MODEL,
  BRAND_DETAIL_ORDER_BALANCE_COUNT_MODEL,
  BRAND_DETAIL_NOTICE_PRODUCT_FILTER_OPTION_MODEL,
  BRAND_DETAIL_NOTICE_PRODUCT_SORT_OPTION_MODEL,
  BRAND_DETAIL_PRODUCT_SUMMARY_MODEL,
  BRAND_DETAIL_SELLER_MODEL,
  BRAND_DETAIL_SNS_ROWS_MODEL,
  BRAND_DETAIL_STATISTIC_KEYWORD_MODEL,
  BRAND_DETAIL_STATUS_MODEL,
  BRAND_EXPOSURE_SELECTOR_FILTER_OPTION_MODEL,
  BRAND_JOIN_LIST_FILTER_OPTION_MODEL,
  BRAND_JOIN_LIST_SORT_OPTION_MODEL,
  BRAND_LIST_FILTER_OPTION_MODEL,
  BRAND_LIST_SORT_OPTION_MODEL,
  BRAND_REJECT_MODAL_MODEL,
  BRAND_TIER_SELECTOR_FILTER_OPTION_MODEL,
  BRAND_DETAIL_TRADING_CONDITION_MODEL,
  BRAND_DETAIL_ADD_DELIVERY_CHARGE_MODEL,
  BRAND_DETAIL_DELIVERY_INFO_MODEL,
  BRAND_DETAIL_AFTER_SERVICE_MODEL,
} from './models';
import { LIST_PAGINATION_MODEL, LIST_SUMMARY_MODEL } from '../app/models';
import {
  BRAND_EXPOSURE_LIST_STATUS,
  BRAND_SNS_NUMBER,
  BRAND_TIER_LIST_STATUS,
  STORE_STATUS,
} from '../../../constants/service/app/brandConstants';
import {
  BALANCE_PERIOD_TYPE,
  DEFAULT_MONTHLY_BALANCE_DAY,
  DEFAULT_MONTHLY_BALANCE_PERIOD,
} from '../../../constants/service/app/balanceConstants';

/**
 *  INITIAL STATE
 */
const initialState = {
  /**
   *  목록 페이지 url
   */
  listPageUrl: null,

  /**
   *  입점승인 판매점 리스트
   */
  brandJoinList: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  검색 옵션
     */
    filterOption: BRAND_JOIN_LIST_FILTER_OPTION_MODEL,
    /**
     *  정렬 옵션 리스트
     */
    sortOption: BRAND_JOIN_LIST_SORT_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: LIST_PAGINATION_MODEL,
    /**
     *  집계 데이터
     */
    summary: LIST_SUMMARY_MODEL,
  },

  /**
   *  현재까지 등록된 브랜드 티어 리스트
   */
  currentTierList: [],

  /**
   *  브랜드 리스트 - 테이블
   */
  brandList: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  브랜드 리스트 - 대시보드
     */
    brandSummaryBox: BRAND_SUMMARY_BOX_MODEL,
    /**
     *  검색 옵션
     */
    filterOption: BRAND_LIST_FILTER_OPTION_MODEL,
    /**
     *  정렬 옵션 리스트
     */
    sortOption: BRAND_LIST_SORT_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: LIST_PAGINATION_MODEL,
    /**
     *  집계 데이터
     */
    summary: LIST_SUMMARY_MODEL,
  },

  /**
   *  브랜드 태그 그룹별 태그 SELECT LIST
   */
  storeTagGroupList: {},

  /**
   *  브랜드 상세정보
   */
  brandDetail: {
    info: {
      storeId: null,
      /**
       *  입점상태 정보
       */
      statusInfo: BRAND_DETAIL_STATUS_MODEL,
      /**
       *  담당자 정보
       */
      sellerInfo: BRAND_DETAIL_SELLER_MODEL,
      /**
       *  브랜드 정보
       */
      brandInfo: BRAND_DETAIL_BRAND_MODEL,
      /**
       *  사업자 정보
       */
      businessInfo: BRAND_DETAIL_BUSINESS_MODEL,
      /**
       *  정산 정보
       */
      balanceInfo: BRAND_DETAIL_BALANCE_MODEL,
      /**
       *  SNS 정보
       **/
      snsInfoRows: BRAND_DETAIL_SNS_ROWS_MODEL,
    },

    statistic: {
      keyword: BRAND_DETAIL_STATISTIC_KEYWORD_MODEL,
      data: [],
    },

    orderSummaryInfo: BRAND_DETAIL_ORDER_BALANCE_COUNT_MODEL,
    balanceSummaryInfo: BRAND_DETAIL_ORDER_BALANCE_CALCULATE_MODEL,

    product: {
      /**
       *  상품 통계 정보
       */
      summaryInfo: BRAND_DETAIL_PRODUCT_SUMMARY_MODEL,
      /**
       *  상품 리스트
       */
      listInfo: {
        list: [],
        summary: {
          totalCnt: 0,
        },
        /**
         *  리스트 pagination
         */
        pagination: LIST_PAGINATION_MODEL,
      },
    },

    noticeProduct: {
      /**
       *  상품 공지사항 리스트
       */
      listInfo: {
        list: [],
        /**
         *  검색 옵션
         */
        filterOption: BRAND_DETAIL_NOTICE_PRODUCT_FILTER_OPTION_MODEL,
        /**
         *  검색 옵션
         */
        sortOption: BRAND_DETAIL_NOTICE_PRODUCT_SORT_OPTION_MODEL,
        /**
         *  리스트 pagination
         */
        pagination: LIST_PAGINATION_MODEL,
      },
      summary: {
        totalCnt: null,
      },
    },

    productRegister: {
      /**
       *  거래조건에 관한 정보
       */
      tradingConditionsInfo: BRAND_DETAIL_TRADING_CONDITION_MODEL,

      /**
       *  추가 배송비 정보
       */
      addDeliveryChargeInfo: BRAND_DETAIL_ADD_DELIVERY_CHARGE_MODEL,

      /**
       * 배송정보
       */
      deliveryInfo: BRAND_DETAIL_DELIVERY_INFO_MODEL,

      /**
       *  A/S 정보
       */
      afterServiceInfo: BRAND_DETAIL_AFTER_SERVICE_MODEL,
    },
  },

  /**
   *  브랜드 입점거절 모달
   */
  rejectBrandModal: {
    isOpen: false,
    data: BRAND_REJECT_MODAL_MODEL,
  },

  /**
   *  브랜드 티어 관리 - 헤더
   */
  brandTierHeader: {
    /**
     *  티어 일괄 변경하기로 선택한 티어
     */
    selectedTier: null,
    isUpdateRecommendList: false,
  },

  /**
   *  브랜드 티어 관리 - 리스트
   */
  brandTierList: {
    /**
     *  브랜드 티어 원본 리스트
     */
    originList: [],
    /**
     *  브랜드 티어 수정된 리스트
     */
    newList: [],
    /**
     *  브랜드 티어 카드 접기/펴기 여부 목록
     */
    toggleList: [],
    /**
     *  티어 변경하려고 선택한 브랜드 리스트
     */
    selectedList: [],
  },

  /**
   *  브랜드 티어 관리 - 셀렉터
   */
  brandTierSelector: {
    /**
     *  검색된 브랜드 목록
     */
    list: [],
    /**
     *  검색 옵션
     */
    filterOption: BRAND_TIER_SELECTOR_FILTER_OPTION_MODEL,
    /**
     *  추가하기로 선택한 티어
     */
    selectedTier: null,
    /**
     *  추가하려고 선택한 브랜드 리스트
     */
    selectedList: [],
  },

  /**
   *  브랜드 노출관리 - 리스트
   */
  brandExposureList: {
    /**
     *  노출된 브랜드 원본 리스트
     */
    originList: [],
    /**
     *  노출된 브랜드 수정된 리스트
     */
    newList: [],
  },

  /**
   *  브랜드 노출관리 - 셀렉터
   */
  brandExposureSelector: {
    /**
     *  검색된 브랜드 목록
     */
    list: [],
    /**
     *  검색옵션
     */
    filterOption: BRAND_EXPOSURE_SELECTOR_FILTER_OPTION_MODEL,
    /**
     *  추가하려고 선택한 브랜드 리스트
     */
    selectedList: [],
  },

  /**
   *  브랜드 태그 거절 모달
   */
  rejectRegistrationStoreTagModal: {
    isOpen: false,
    data: {},
  },
};

/**
 *  REDUCER
 */
export default handleActions(
  {
    /**
     *  [브랜드] 브랜드 페이지 데이터 변경하기
     */
    [actions.SET_DATA]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(action.payload.path, action.payload.value, draft);
      });
    },

    /**
     *  [판매점] 입점승인 판매점 리스트 요청하기
     */
    [actions.GET_BRAND_JOIN_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  입점승인 판매점 리스트
         */
        draft.brandJoinList = action.payload.brandJoinList;
      });
    },

    /**
     *  [브랜드] 브랜드 리스트 페이지 전체 데이터 요청하기
     */
    [actions.GET_BRAND_ALL_INFO_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  현재까지 등록된 브랜드 티어 리스트
         */
        draft.currentTierList = action.payload.currentTierList.map((item) => {
          return { id: item, name: item };
        });
        /**
         *  브랜드 리스트, Summary box
         */
        draft.brandList = {
          ...action.payload.brandList,
          brandSummaryBox: action.payload.brandSummaryBox,
        };
      });
    },

    /**
     *  [브랜드] 브랜드 리스트 요청하기
     */
    [actions.GET_BRAND_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  브랜드 리스트 - 테이블
         */
        draft.brandList = {
          brandSummaryBox: draft.brandList.brandSummaryBox,
          ...action.payload.brandList,
        };
      });
    },

    /**
     *  [브랜드] 브랜드 상세 > 브랜드 정보 조회하기
     */
    [actions.GET_BRAND_DETAIL_INFO_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         * 브랜드 정보 탭
         */
        draft.brandDetail.info = clone({
          ...action.payload.info,
          balanceInfo: {
            ...draft.brandDetail.info.balanceInfo,
            ...action.payload.info.balanceInfo,
            balancePeriodInfoList: action.payload.info.balanceInfo.balancePeriodInfoList.length
              ? state.brandDetail.info.balanceInfo.balancePeriodInfoList.map((item, index) =>
                  action.payload.info.balanceInfo.balancePeriodInfoList[index] &&
                  action.payload.info.balanceInfo.balancePeriodInfoList[index].balancePeriodType ===
                    BALANCE_PERIOD_TYPE.MONTHLY
                    ? {
                        ...item,
                        ...action.payload.info.balanceInfo.balancePeriodInfoList[index],
                      }
                    : {
                        ...item,
                        ...action.payload.info.balanceInfo.balancePeriodInfoList[index],
                        balanceDay: DEFAULT_MONTHLY_BALANCE_DAY,
                        balancePeriod: DEFAULT_MONTHLY_BALANCE_PERIOD,
                      },
                )
              : state.brandDetail.info.balanceInfo.balancePeriodInfoList,
            brandFeeInfoList: action.payload.info.balanceInfo.brandFeeInfoList
              ? state.brandDetail.info.balanceInfo.brandFeeInfoList.map((item, index) => ({
                  ...item,
                  ...action.payload.info.balanceInfo.brandFeeInfoList[index],
                }))
              : state.brandDetail.info.balanceInfo.brandFeeInfoList,
            amondzFeeInfoList: action.payload.info.balanceInfo.amondzFeeInfoList
              ? state.brandDetail.info.balanceInfo.amondzFeeInfoList.map((item, index) => ({
                  ...item,
                  ...action.payload.info.balanceInfo.amondzFeeInfoList[index],
                }))
              : state.brandDetail.info.balanceInfo.amondzFeeInfoList,
          },
        });

        /**
         *  브랜드 태그 분류에 따라서
         *  태그 검수 리스트 타입 설정
         */
        let confirmTagButtonType = null;
        const tagGroupTagList = STORE_TAG_GROUP_KIND_LIST.reduce((accumulator, tagGroupType) => {
          if (!draft.brandDetail.info.brandInfo[tagGroupType]) {
            return accumulator;
          }

          confirmTagButtonType = CONFIRM_TAG_LIST_TYPE.ACTIVE;
          if (draft.brandDetail.info.statusInfo.status !== STORE_STATUS.ACTIVATE) {
            if (draft.brandDetail.info.brandInfo[tagGroupType][CONFIRM_TAG_LIST_TYPE.CONFIRM_READY].length) {
              confirmTagButtonType = CONFIRM_TAG_LIST_TYPE.CONFIRM_READY;
            } else if (draft.brandDetail.info.brandInfo[tagGroupType][CONFIRM_TAG_LIST_TYPE.REJECT].length) {
              confirmTagButtonType = CONFIRM_TAG_LIST_TYPE.REJECT;
            }
          }

          accumulator[tagGroupType] = {
            ...draft.brandDetail.info.brandInfo[tagGroupType],
            confirmTagButtonType,
          };
          return accumulator;
        }, {});
        draft.brandDetail.info.brandInfo = {
          ...draft.brandDetail.info.brandInfo,
          ...tagGroupTagList,
        };

        /**
         *  브랜드 태그 그룹별 태그
         */
        draft.storeTagGroupList = action.payload.info.storeTagGroupData.selectTagList;

        /**
         *  브랜드 메인 이미지에 수정여부체크(isEdit) 데이터 추가
         */
        if (action.payload.info.brandInfo.mainImg.length) {
          /**
           *  데이터가 있을때만 수정여부체크(isEdit) 데이터 추가
           */
          draft.brandDetail.info.brandInfo.mainImg[0] = {
            isEdit: false,
            ...action.payload.info.brandInfo.mainImg[0],
          };
        } else {
          draft.brandDetail.info.brandInfo.mainImg = [];
        }

        /**
         *  브랜드 서브 이미지에 수정여부체크(isEdit) 데이터 추가
         */
        if (action.payload.info.brandInfo.subImg.length) {
          /**
           *  데이터가 있을때만 수정여부체크(isEdit) 데이터 추가
           */
          draft.brandDetail.info.brandInfo.subImg[0] = {
            isEdit: false,
            ...action.payload.info.brandInfo.subImg[0],
          };
        } else {
          draft.brandDetail.info.brandInfo.subImg = [];
        }

        /**
         *  사업자 등록 번호 이미지에 수정여부체크(isEdit) 데이터 추가
         */
        if (action.payload.info.businessInfo.businessLicenseImg.length) {
          /**
           *  데이터가 있을때만 수정여부체크(isEdit) 데이터 추가
           */
          draft.brandDetail.info.businessInfo.businessLicenseImg[0] = {
            isEdit: false,
            ...action.payload.info.businessInfo.businessLicenseImg[0],
          };
        } else {
          draft.brandDetail.info.businessInfo.businessLicenseImg = [];
        }

        /**
         *  통신판매업 신고 이미지에 수정여부체크(isEdit) 데이터 추가
         */
        if (action.payload.info.businessInfo.onlineSalesRegImg.length) {
          /**
           *  데이터가 있을때만 수정여부체크(isEdit) 데이터 추가
           */
          draft.brandDetail.info.businessInfo.onlineSalesRegImg[0] = {
            isEdit: false,
            ...action.payload.info.businessInfo.onlineSalesRegImg[0],
          };
        } else {
          draft.brandDetail.info.businessInfo.onlineSalesRegImg = [];
        }

        /**
         *  통장사본 이미지에 수정여부체크(isEdit) 데이터 추가
         */
        if (action.payload.info.balanceInfo.bankBookCopyImg.length) {
          /**
           *  데이터가 있을때만 수정여부체크(isEdit) 데이터 추가
           */
          draft.brandDetail.info.balanceInfo.bankBookCopyImg[0] = {
            isEdit: false,
            ...action.payload.info.balanceInfo.bankBookCopyImg[0],
          };
        } else {
          draft.brandDetail.info.balanceInfo.bankBookCopyImg = [];
        }

        /**
         * sns 정보 초기화
         */
        draft.brandDetail.info.snsInfoRows = clone(BRAND_DETAIL_SNS_ROWS_MODEL);

        /**
         *  서버에서 내려준 배열에서 no가 각각 1, 2, 3, 그리고 4 이상인 요소로 서로 다른 배열 생성
         */
        const instagramInfo = filter(action.payload.info.snsInfoRows, (item) => item.no === BRAND_SNS_NUMBER.INSTAGRAM);
        const facebookInfo = filter(action.payload.info.snsInfoRows, (item) => item.no === BRAND_SNS_NUMBER.FACEBOOK);
        const blogInfo = filter(action.payload.info.snsInfoRows, (item) => item.no === BRAND_SNS_NUMBER.BLOG);
        const etcRows = filter(action.payload.info.snsInfoRows, (item) => item.no >= 4);

        /**
         *  각 배열에 데이터가 있으면 해당하는 인덱스로 데이터를 넣어줌
         */
        if (instagramInfo.length) draft.brandDetail.info.snsInfoRows[0] = instagramInfo[0];
        if (facebookInfo.length) draft.brandDetail.info.snsInfoRows[1] = facebookInfo[0];
        if (blogInfo.length) draft.brandDetail.info.snsInfoRows[2] = blogInfo[0];
        if (etcRows.length) etcRows.map((item, index) => (draft.brandDetail.info.snsInfoRows[index + 3] = item));
      });
    },

    /**
     *  [브랜드] 브랜드 상세 > 통계 정보 조회하기
     */
    [actions.GET_BRAND_DETAIL_STATISTIC_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.brandDetail.statistic.data = action.payload.statisticData;
      });
    },

    /**
     *  [브랜드] 브랜드 상세 > 정산 정보 조회하기
     */
    [actions.GET_BRAND_DETAIL_ORDER_BALANCE_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.brandDetail.orderSummaryInfo = action.payload.orderSummaryInfo;
        draft.brandDetail.balanceSummaryInfo = action.payload.balanceSummaryInfo;
      });
    },

    /**
     *  [브랜드] 브랜드 상세 > 상품 정보 조회하기
     */
    [actions.GET_BRAND_DETAIL_PRODUCT_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.brandDetail.product.listInfo = action.payload.listInfo;
        if (action.payload.summaryInfo) draft.brandDetail.product.summaryInfo = action.payload.summaryInfo;
      });
    },

    /**
     *  [브랜드] 브랜드 상세 > 공지사항 정보 조회하기
     */
    [actions.GET_BRAND_DETAIL_NOTICE_PRODUCT_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        const { summary, ...listInfo } = action.payload;
        draft.brandDetail.noticeProduct.listInfo = listInfo;
        draft.brandDetail.noticeProduct.summary = summary;
      });
    },

    /**
     *  [브랜드] 브랜드 상세 > 상품등록 정보 조회하기
     */
    [actions.GET_BRAND_DETAIL_PRODUCT_REGISTER_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.brandDetail.productRegister = produce(action.payload, (draft) => {
          draft.addDeliveryChargeInfo.deliveryChargeReturn = 3000;
        });
      });
    },

    /**
     *  [브랜드] 브랜드 티어 정보 요청하기
     */
    [actions.GET_BRAND_TIER_INFO_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  헤더 정보 초기화
         */
        draft.brandTierHeader.selectedTier = null;
        draft.brandTierHeader.isUpdateRecommendList = false;

        /**
         *  셀렉터 정보 초기화
         */
        draft.brandTierSelector.list = [];
        draft.brandTierSelector.filterOption = BRAND_TIER_SELECTOR_FILTER_OPTION_MODEL;
        draft.brandTierSelector.selectedTier = null;
        draft.brandTierSelector.selectedList = [];

        /**
         *  현재까지 등록된 브랜드 티어 리스트
         */
        draft.currentTierList = action.payload.currentTierList.map((item) => {
          return { id: item, name: item };
        });

        /**
         *  브랜드 티어 카드 접기/펴기 여부 목록
         */
        draft.brandTierList.toggleList = action.payload.currentTierList.map(() => true);

        /**
         *  브랜드 티어 원본 리스트
         */
        draft.brandTierList.originList = action.payload.brandTierList;

        /**
         *  브랜드 티어 수정된 리스트
         */
        draft.brandTierList.newList = action.payload.brandTierList.map((tierList) =>
          tierList.map((item) => ({
            ...item,
            listStatus: BRAND_TIER_LIST_STATUS.NORMAL,
          })),
        );

        /**
         *  브랜드 셀렉터 리스트
         */
        draft.brandTierSelector.list = action.payload.brandSelectorList;
      });
    },

    /**
     *  [브랜드] 브랜드 티어 셀렉터 검색하기
     */
    [actions.GET_BRAND_TIER_SELECTOR_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  브랜드 셀렉터 리스트
         */
        draft.brandTierSelector.list = action.payload.brandSelectorList;
      });
    },

    /**
     *  [브랜드] 브랜드 노출 리스트 정보 요청하기
     */
    [actions.GET_BRAND_TOP_EXPOSURE_INFO_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  브랜드 노출 원본 리스트
         */
        draft.brandExposureList.originList = action.payload.brandExposureList;
        /**
         *  브랜드 노출 수정된 리스트
         */
        draft.brandExposureList.newList = action.payload.brandExposureList.map((item) => {
          return {
            ...item,
            listStatus: BRAND_EXPOSURE_LIST_STATUS.NORMAL,
          };
        });
        /**
         *  데이터 초기화
         */
        draft.brandExposureSelector.list = action.payload.brandSelectorList;
        draft.brandExposureSelector.filterOption = BRAND_EXPOSURE_SELECTOR_FILTER_OPTION_MODEL;
        draft.brandExposureSelector.selectedList = [];
      });
    },

    /**
     *  [브랜드] 브랜드 노출 리스트 셀렉터 검색하기
     */
    [actions.GET_BRAND_EXPOSURE_SELECTOR_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  브랜드 셀렉터 리스트
         */
        draft.brandExposureSelector.list = action.payload.brandSelectorList;
      });
    },

    /**
     *  브랜드 태그 승인하기
     */
    [actions.PERMIT_REGISTRATION_STORE_TAG_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.brandDetail.info.brandInfo[action.payload.tagGroupType] = action.payload.confirmTagData;
      });
    },

    /**
     *  브랜드 태그 거절하기
     */
    [actions.REJECT_REGISTRATION_STORE_TAG_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.brandDetail.info.brandInfo[action.payload.tagGroupType] = {
          ...draft.brandDetail.info.brandInfo[action.payload.tagGroupType],
          rejectInfo: action.payload.rejectInfo,
          [CONFIRM_TAG_LIST_TYPE.CONFIRM_READY]: [],
          [CONFIRM_TAG_LIST_TYPE.REJECT]:
            draft.brandDetail.info.brandInfo[action.payload.tagGroupType][CONFIRM_TAG_LIST_TYPE.CONFIRM_READY],
          confirmTagButtonType: CONFIRM_TAG_LIST_TYPE.REJECT,
        };
      });
    },

    /**
     *  모달 토글
     */
    [actions.TOGGLE_MODAL]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath([action.payload.name, 'data'], action.payload.data, draft);
        updateStoreWithPath([action.payload.name, 'isOpen'], action.payload.value, draft);
      });
    },
  },
  initialState,
);
